import { useRef, useState } from "react"

import { useParams } from "react-router-dom"

import { useMutation, useQuery } from "@tanstack/react-query"

import {
    AlertTriangle,
    CheckCircle2,
    Clock,
    ExternalLink,
    File,
    FileText,
    MessageSquare,
    Send,
    ShieldCheck,
    Trash2,
    Upload,
} from "lucide-react"

import Overlay from "../../Components/Overlay"
import PublicHeader from "../../publicHeader"

import preAdverseActionResponseContextQuery from "./services/pre-adverse-action-response-context"
import submitPreAdverseActionResponseMutation from "./services/submit-pre-adverse-action-response"

const MAX_DOCUMENTS = 20

const ALLOWED_EXTENSIONS = ["pdf", "png", "jpg", "jpeg"]

const formatDateTime = (date) => {
    if (!date) return null
    const parsed = new Date(date)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

const splitFileName = (filename) => {
    const lastDot = filename.lastIndexOf('.')
    if (lastDot === -1) return { name: filename, extension: '' }
    return { name: filename.slice(0, lastDot), extension: filename.slice(lastDot + 1) }
}

// Shared page chrome: centered card with the TruCheck shield header.
const PageShell = ({ children }) => (
    <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="flex flex-col items-center px-4 py-10">
            <div className="w-full max-w-2xl">
                <div className="flex flex-col items-center text-center mb-8">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl flex items-center justify-center mb-4">
                        <ShieldCheck className="w-6 h-6 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Pre-Adverse Action Response</h1>
                </div>
                {children}
            </div>
        </div>
    </div>
)

// Reference number / expiry strip shown on top of the form and submitted views.
const ContextStrip = ({ context }) => (
    <div className="flex flex-wrap gap-x-8 gap-y-2 px-5 py-3 bg-slate-50 border-b border-slate-200/70 rounded-t-2xl">
        {context?.referenceNumber && (
            <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500">Reference Number</span>
                <span className="text-sm font-semibold text-slate-800 font-mono tracking-wide">{context.referenceNumber}</span>
            </div>
        )}
        {context?.companyName && (
            <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500">Company</span>
                <span className="text-sm font-semibold text-slate-800">{context.companyName}</span>
            </div>
        )}
    </div>
)

// A simple list of links to submitted documents.
const DocumentLinks = ({ documents }) => (
    <ul className="space-y-2 mt-1">
        {documents.map((document, i) => (
            <li key={i}>
                <a
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-blue-700 hover:underline"
                >
                    <File className="w-4 h-4 shrink-0" />
                    {document.fileName || `Document ${i + 1}`}
                    <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                </a>
            </li>
        ))}
    </ul>
)

// The read-only view of an already-submitted response (also shown right after
// submitting): submission date, the written explanation, and the document links.
const SubmittedView = ({ context }) => (
    <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
        <ContextStrip context={context} />
        <div className="p-6 space-y-5">
            <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm font-semibold text-emerald-800">Your response has been submitted</p>
                    <p className="text-xs text-emerald-700 mt-1">
                        Submitted on <span className="font-semibold">{formatDateTime(context?.submittedAt)}</span>.
                        A confirmation email has been sent to you, and your submission has been shared with the employer.
                        No further changes can be made.
                    </p>
                </div>
            </div>

            {context?.comment && (
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4 text-gray-400" />
                        <p className="text-sm font-semibold text-gray-700">Your Explanation</p>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap rounded-lg border border-gray-100 bg-gray-50 p-4">
                        {context.comment}
                    </p>
                </div>
            )}

            {(context?.documents || []).length > 0 && (
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <p className="text-sm font-semibold text-gray-700">Submitted Documents</p>
                    </div>
                    {(context?.requestedUploadItems || []).length > 0 ? (
                        <div className="space-y-3">
                            {context.requestedUploadItems.map((item) => {
                                const files = context.documents.filter((document) => document.requestedItemId === item.id)
                                return (
                                    <div key={item.id}>
                                        <p className="text-sm font-medium text-gray-700">{item.label || "Requested document"}</p>
                                        {files.length > 0
                                            ? <DocumentLinks documents={files} />
                                            : <p className="text-xs text-gray-400 mt-1">No files provided.</p>}
                                    </div>
                                )
                            })}
                            {(() => {
                                const extras = context.documents.filter((document) => !document.requestedItemId)
                                if (!extras.length) return null
                                return (
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Additional documents</p>
                                        <DocumentLinks documents={extras} />
                                    </div>
                                )
                            })()}
                        </div>
                    ) : (
                        <DocumentLinks documents={context.documents} />
                    )}
                </div>
            )}
        </div>
    </div>
)

// A drag-and-drop / click upload zone with its own hidden input, so multiple zones can coexist
// (one per requested document slot, plus the free "additional documents" area).
const UploadZone = ({ onFile, disabled }) => {
    const inputRef = useRef()
    return (
        <div
            onClick={() => !disabled && inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (!disabled) onFile(e.dataTransfer.files[0]) }}
            className={`border-2 border-dashed border-gray-300 rounded-xl p-5 text-center transition ${disabled ? "opacity-60" : "cursor-pointer hover:bg-gray-50"}`}
        >
            <Upload className="w-5 h-5 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600 font-medium text-sm">Drag and drop or click to add a file</p>
            <p className="text-gray-400 text-xs mt-1">Supported file types: .pdf, .png, .jpg</p>
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => { e.preventDefault(); onFile(e.target.files[0]); e.target.value = "" }}
                className="hidden"
            />
        </div>
    )
}

// The list of locally-selected files (with a remove button) for one slot.
const SelectedFiles = ({ files, onRemove }) => {
    if (!files.length) return null
    return (
        <ul className="space-y-2 mb-2">
            {files.map((document) => (
                <li key={document.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-center gap-2 min-w-0">
                        <File className="text-gray-500 flex-shrink-0" size={18} />
                        <span className="text-sm font-medium text-gray-700 truncate" title={document.originalName}>{document.originalName}</span>
                    </div>
                    <button
                        onClick={() => onRemove(document.id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-red-50 text-red-600 px-2.5 py-1.5 text-sm font-medium hover:bg-red-100 active:scale-95 transition flex-shrink-0"
                    >
                        <Trash2 size={15} />
                        Remove
                    </button>
                </li>
            ))}
        </ul>
    )
}

// One-time submission confirmation dialog.
const ConfirmSubmitDialog = ({ documentCount, hasComment, isSubmitting, onConfirm, onCancel }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-5">
            <div className="space-y-1">
                <h2 className="text-xl font-bold text-gray-800">Submit Your Response?</h2>
                <p className="text-sm text-gray-600">
                    You are about to submit {documentCount > 0 ? `${documentCount} document${documentCount === 1 ? "" : "s"}` : "no documents"}
                    {hasComment ? " and a written explanation" : ""}.
                </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <p className="text-xs text-amber-800">
                    <strong>This is a one-time submission.</strong> Once submitted, you will not be able to
                    make changes or submit again.
                </p>
            </div>

            <div className="flex gap-3 pt-1">
                <button
                    onClick={onCancel}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-all focus:outline-none disabled:opacity-60"
                >
                    Go Back
                </button>
                <button
                    onClick={onConfirm}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-all focus:outline-none disabled:opacity-60"
                >
                    {isSubmitting
                        ? (documentCount > 0 ? "Uploading & submitting..." : "Submitting...")
                        : "Yes, Submit"}
                </button>
            </div>
        </div>
    </div>
)

const PreAdverseActionResponse = () => {
    const { responseKey } = useParams()

    // Locally-selected documents (not uploaded until submit):
    // { id, file, fileName, fileExtension, originalName }.
    const [documents, setDocuments] = useState([])
    const [comment, setComment] = useState("")
    const [formError, setFormError] = useState("")
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)

    const {
        data: context,
        isFetching: isContextLoading,
        error: contextError,
        refetch: refetchContext,
    } = useQuery({
        queryKey: ['pre-adverse-action-response-context', responseKey],
        queryFn: () => preAdverseActionResponseContextQuery(responseKey),
        refetchOnWindowFocus: false,
        retry: false,
        enabled: !!responseKey,
    })

    const {
        isPending: isSubmitting,
        error: submitError,
        mutate: submitResponse,
    } = useMutation({
        mutationFn: submitPreAdverseActionResponseMutation,
        onSuccess: () => {
            setIsConfirmOpen(false)
            // The context now resolves to "submitted" and carries the saved content.
            refetchContext()
        },
        onError: () => {
            setIsConfirmOpen(false)
        },
    })

    // Files are only registered locally here; the actual upload happens at submit
    // time inside the submit service (same pattern as the create-TruCheck dialog).
    // `requestedItemId` tags a file to the QA-requested upload slot it answers (null = a free extra).
    const handleFile = (file, requestedItemId = null) => {
        if (!file) return
        if (documents.length >= MAX_DOCUMENTS) {
            setFormError(`You can attach up to ${MAX_DOCUMENTS} documents.`)
            return
        }
        const { name, extension } = splitFileName(file.name)
        if (!ALLOWED_EXTENSIONS.includes(extension.toLowerCase())) {
            setFormError(`Unsupported file type: "${file.name}". Please add a .pdf, .png or .jpg file.`)
            return
        }
        // The same file (same name and size) can only be attached once per slot.
        const isDuplicate = documents.some(
            (document) => document.originalName === file.name && document.file?.size === file.size && document.requestedItemId === requestedItemId
        )
        if (isDuplicate) {
            setFormError(`"${file.name}" has already been added.`)
            return
        }
        setFormError("")
        setDocuments((current) => [
            ...current,
            {
                id: crypto.randomUUID(),
                file,
                fileName: name,
                fileExtension: extension,
                originalName: file.name,
                requestedItemId,
            },
        ])
    }

    const handleRemoveDocument = (id) => {
        setDocuments((current) => current.filter((document) => document.id !== id))
    }

    const handleOpenConfirm = () => {
        if (!comment.trim() && documents.length === 0) {
            setFormError("Please add a written explanation or at least one document before submitting.")
            return
        }
        setFormError("")
        setIsConfirmOpen(true)
    }

    if (isContextLoading && !context) {
        return (
            <PageShell>
                <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg p-6">
                    <p className="text-sm text-gray-500">Loading...</p>
                </div>
            </PageShell>
        )
    }

    // Invalid / unknown link.
    if (contextError || !context) {
        return (
            <PageShell>
                <div className="bg-white rounded-2xl border-2 border-red-200 shadow-lg p-6 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-red-700">This link is not valid</p>
                        <p className="text-xs text-gray-500 mt-1">
                            Please make sure you opened the exact link from your email. If the problem
                            persists, reply to the notice email you received.
                        </p>
                    </div>
                </div>
            </PageShell>
        )
    }

    if (context.status === "submitted") {
        return (
            <PageShell>
                <SubmittedView context={context} />
            </PageShell>
        )
    }

    if (context.status === "expired") {
        return (
            <PageShell>
                <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
                    <ContextStrip context={context} />
                    <div className="p-6 flex items-start gap-3">
                        <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-gray-800">The response window has closed</p>
                            <p className="text-xs text-gray-500 mt-1">
                                The period to submit a response to this pre-adverse action notice has ended
                                {context.expiresAt ? ` on ${formatDateTime(context.expiresAt)}` : ""}. If you still wish
                                to provide information, please reply to the notice email you received.
                            </p>
                        </div>
                    </div>
                </div>
            </PageShell>
        )
    }

    // status === "open" — the one-time response form.
    return (
        <PageShell>
            <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
                <ContextStrip context={context} />
                <div className="p-6 space-y-6">

                    <div className="space-y-2">
                        <p className="text-sm text-gray-700">
                            {context.candidateName ? <>Dear <strong>{context.candidateName}</strong>, </> : null}
                            you can provide additional context regarding the pre-adverse action notice you
                            received: upload supporting documents (PDF or image) and/or add a written explanation.
                        </p>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
                            <p className="text-xs text-amber-800">
                                <strong>One-time submission:</strong> you can submit this form only once, and it
                                cannot be changed afterwards.
                            </p>
                            {context.expiresAt && (
                                <p className="text-xs text-amber-800">
                                    <strong>Deadline:</strong> this form is available until{" "}
                                    <span className="font-semibold">{formatDateTime(context.expiresAt)}</span>.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* If QA also requested live photo-ID verification session(s), they are handled via
                        the button links in the email — not this form. Surface a note so the candidate
                        knows to check their email. */}
                    {(context.veriffSessionCount || 0) > 0 && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                            <p className="text-xs text-indigo-800">
                                You also have <strong>{context.veriffSessionCount} live photo-ID verification session
                                {context.veriffSessionCount === 1 ? "" : "s"}</strong> to complete. Please use the
                                verification button{context.veriffSessionCount === 1 ? "" : "s"} in the email you received
                                to start {context.veriffSessionCount === 1 ? "it" : "them"} — separately from this form.
                            </p>
                        </div>
                    )}

                    {/* REQUESTED DOCUMENTS — one labeled upload slot per document QA asked for. */}
                    {(context.requestedUploadItems || []).length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-400" />
                                <p className="text-sm font-semibold text-gray-700">Requested Documents</p>
                            </div>
                            {context.requestedUploadItems.map((item) => {
                                const slotFiles = documents.filter((document) => document.requestedItemId === item.id)
                                return (
                                    <div key={item.id} className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-2">
                                        <p className="text-sm font-semibold text-gray-800">{item.label || "Requested document"}</p>
                                        {item.comment && <p className="text-xs text-gray-600 whitespace-pre-wrap">{item.comment}</p>}
                                        <SelectedFiles files={slotFiles} onRemove={handleRemoveDocument} />
                                        {documents.length < MAX_DOCUMENTS && (
                                            <UploadZone onFile={(file) => handleFile(file, item.id)} />
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {/* ADDITIONAL / FREE DOCUMENTS */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <p className="text-sm font-semibold text-gray-700">
                                {(context.requestedUploadItems || []).length > 0 ? "Additional Documents" : "Supporting Documents"}
                            </p>
                            <span className="text-xs text-gray-400">(optional, up to {MAX_DOCUMENTS} total)</span>
                        </div>

                        <SelectedFiles files={documents.filter((document) => !document.requestedItemId)} onRemove={handleRemoveDocument} />

                        {documents.length < MAX_DOCUMENTS && (
                            <UploadZone onFile={(file) => handleFile(file, null)} />
                        )}
                    </div>

                    {/* EXPLANATION */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-gray-400" />
                            <p className="text-sm font-semibold text-gray-700">Your Explanation</p>
                        </div>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={6}
                            maxLength={5000}
                            placeholder="Explain anything you believe the employer should consider before a final decision is made (e.g. context on the reviewed information, corrections, or details about the documents you attached)."
                            className="w-full rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition p-3 text-sm"
                        />
                        <p className="text-xs text-gray-400 text-right">{comment.length}/5000</p>
                    </div>

                    {formError && (
                        <p className="text-sm text-red-600">{formError}</p>
                    )}

                    {submitError && (
                        <p className="text-sm text-red-600">
                            {submitError?.response?.data?.error || "Could not submit your response. Please try again."}
                        </p>
                    )}

                    <button
                        type="button"
                        onClick={handleOpenConfirm}
                        disabled={isSubmitting}
                        className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                        <Send className="w-4 h-4" />
                        Submit Additional Documentation
                    </button>

                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        Please note that <span className="font-semibold">Photo ID Verification</span> needs to be submitted separately.
                    </p>

                </div>
            </div>

            {isConfirmOpen && (
                <ConfirmSubmitDialog
                    documentCount={documents.length}
                    hasComment={!!comment.trim()}
                    isSubmitting={isSubmitting}
                    onConfirm={() => submitResponse({
                        responseKey,
                        comment: comment.trim(),
                        documents,
                    })}
                    onCancel={() => setIsConfirmOpen(false)}
                />
            )}

            {isSubmitting && <Overlay />}
        </PageShell>
    )
}

export default PreAdverseActionResponse
